import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";

import iconShadow from "leaflet/dist/images/marker-shadow.png";
import seenIconUrl from "../images/ic_deploy_blue.png";
import bronzeIconUrl from "../images/ic_deploy_bronze.png";
import silverIconUrl from "../images/ic_deploy_silver.png";
import goldIconUrl from "../images/ic_deploy_gold.png";

// Icons auf Modulebene
const ICON_SIZE = [60, 52];
const ICON_ANCHOR = [30, 52];
const POPUP_ANCHOR = [0, -52];

const ICONS = {
  seen: L.icon({ iconUrl: seenIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
  bronze: L.icon({ iconUrl: bronzeIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
  silber: L.icon({ iconUrl: silverIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
  gold: L.icon({ iconUrl: goldIconUrl, shadowUrl: iconShadow, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR, popupAnchor: POPUP_ANCHOR }),
};

const TYPE_COLORS = {
  gold: "#FFD700",
  silber: "#A0A0A0",
  bronze: "#CD7F32",
  seen: "#1976d2",
};

const TYPE_LABELS = {
  gold: "Gold",
  silber: "Silber",
  bronze: "Bronze",
  seen: "Gesehen",
};

const LocateButton = () => {
  const map = useMap();
  return (
    <Fab
      size="medium"
      onClick={() =>
        navigator.geolocation.getCurrentPosition(
          (pos) => map.flyTo([pos.coords.latitude, pos.coords.longitude], 18),
          () => {}
        )
      }
      sx={{
        position: "absolute",
        bottom: 90,
        right: 16,
        zIndex: 1000,
        backgroundColor: "white",
        color: "black",
        boxShadow: 3,
        "&:hover": { backgroundColor: "#f5f5f5" },
      }}
    >
      <GpsFixedIcon />
    </Fab>
  );
};

const MapAutoLocate = () => {
  const map = useMap();
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => map.flyTo([pos.coords.latitude, pos.coords.longitude], 15),
      () => {}
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const ArenamapExtern = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gyms, setGyms] = useState([]);
  const [selectedGym, setSelectedGym] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchGyms = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`/api/users/gyms/extern/${id}/get-gyms`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          setGyms(await response.json());
        }
      } catch (err) {
        console.error("Fehler beim Laden der Arenen:", err);
      }
    };
    fetchGyms();
  }, [id]);

  const handleGymClick = (gym) => {
    setSelectedGym(gym);
    setShowModal(true);
  };

  return (
    <div style={{ position: "relative", height: "100dvh", width: "100%" }}>
      <MapContainer
        center={[51.1657, 10.4515]}
        zoom={6}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <MapAutoLocate />
        <LocateButton />
        {gyms.map((gym) => (
          <Marker
            key={gym.id}
            position={[gym.latitude, gym.longitude]}
            icon={ICONS[gym.type] || ICONS.seen}
            eventHandlers={{ click: () => handleGymClick(gym) }}
          />
        ))}
      </MapContainer>

      {/* Zurück-Button */}
      <IconButton
        onClick={() => navigate("/members")}
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 1000,
          backgroundColor: "white",
          color: "error.main",
          boxShadow: 3,
          "&:hover": { backgroundColor: "#ffeaea" },
        }}
      >
        <HighlightOffIcon sx={{ fontSize: "2rem" }} />
      </IconButton>

      {/* Arena-Info Bottom Sheet */}
      {showModal && selectedGym && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            bgcolor: "background.paper",
            borderRadius: "20px 20px 0 0",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.18)",
            p: 3,
            pt: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "divider" }} />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Typography variant="h6">{selectedGym.gymname}</Typography>
            <Chip
              label={TYPE_LABELS[selectedGym.type] || "Gesehen"}
              size="small"
              sx={{
                bgcolor: TYPE_COLORS[selectedGym.type] || TYPE_COLORS.seen,
                color: "white",
                fontWeight: 600,
              }}
            />
          </Box>

          {selectedGym.note && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedGym.note}
            </Typography>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={() => setShowModal(false)}
          >
            Schließen
          </Button>
        </Box>
      )}
    </div>
  );
};

export default ArenamapExtern;
